/**
 * VerifiedPoolPage — Multi-select verified leads ready to assign.
 *
 * Apr 28, 2026 (Squad-D). Screen 04 of 12.
 *
 * - Table with row checkboxes; sticky bulk-action bar at top when any selected
 * - "Assign N leads" → opens TeamPickerModal
 * - Per-row "Assign →" picker also available
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, ArrowRight } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  PageShell,
  EmptyState,
  type Lead,
  type Team,
  unwrapList,
  leadName,
  leadPhone,
  leadProduct,
  leadRegion,
  relativeTime,
} from './_lmShared';
import { TeamPickerModal } from './TeamPickerModal';

export function VerifiedPoolPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [singlePickLeadId, setSinglePickLeadId] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);

  useEffect(() => {
    setLoading(true);
    // Apr 28 evening — wired to real backend endpoints. `scope=unassigned`
    // resolves to: assignedToId IS NULL AND verificationStatus IN
    // (chatkart_verified, manually_verified, auto_verified) — exactly the
    // "verified pool ready for Lead Manager triage" set.
    safeGet<unknown>('/lead-integrations/lead-imports?scope=unassigned&limit=100').then((r) => {
      const raw = (r.data as any)?.imports ?? r.data;
      setRows(unwrapList<Lead>(raw));
      setError(r.error);
      setLoading(false);
    });
    safeGet<unknown>('/teams').then((r) => {
      const raw = (r.data as any)?.teams ?? r.data;
      setTeams(unwrapList<Team>(raw));
    });
  }, []);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }

  const targetLeadIds = useMemo(() => {
    if (singlePickLeadId) return [singlePickLeadId];
    return Array.from(selected);
  }, [singlePickLeadId, selected]);

  function handleAssigned() {
    // Remove assigned leads from view.
    const idsToRemove = new Set(targetLeadIds);
    setRows((prev) => prev.filter((r) => !idsToRemove.has(r.id)));
    setSelected(new Set());
    setSinglePickLeadId(null);
    setPickerOpen(false);
  }

  return (
    <PageShell
      title="Verified pool"
      sub={`${rows.length} lead${rows.length === 1 ? '' : 's'} ready to assign · multi-select to bulk-assign`}
      right={
        <>
          <button className="btn btn-outline">
            <Filter size={13} /> Filter
          </button>
          <button
            className="btn btn-primary"
            disabled={selected.size === 0}
            onClick={() => {
              setSinglePickLeadId(null);
              setPickerOpen(true);
            }}
          >
            <ArrowRight size={13} /> Assign {selected.size > 0 ? `${selected.size} leads` : ''}
          </button>
        </>
      }
      scroll={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {/* Sticky bulk action bar */}
        {selected.size > 0 && (
          <div
            style={{
              padding: '10px 24px',
              background: 'var(--ink-0)',
              color: 'var(--ivory-0)',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flexShrink: 0,
            }}
          >
            <span style={{ fontWeight: 500 }}>{selected.size} selected</span>
            <span style={{ opacity: 0.7 }}>·</span>
            <span style={{ opacity: 0.7 }}>Bulk: assign team · transfer ownership · reject</span>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setSelected(new Set())}
              className="btn btn-sm"
              style={{ background: 'rgba(255,255,255,.1)', color: '#fff', border: 'none' }}
            >
              Clear
            </button>
            <button
              className="btn btn-sm"
              style={{ background: 'var(--saffron)', color: '#fff', border: 'none' }}
              onClick={() => {
                setSinglePickLeadId(null);
                setPickerOpen(true);
              }}
            >
              Open team picker
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <EmptyState title="Loading…" />
          ) : error ? (
            <EmptyState
              title="Verified pool not available yet"
              sub="Backend endpoint isn't online. Squad-A is shipping this; once live, verified-and-unassigned leads will show here."
            />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Pool is empty"
              sub="Verify leads from the Unverified queue and they will appear here ready to be assigned to a team."
            />
          ) : (
            <table className="tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selected.size === rows.length && rows.length > 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Product</th>
                  <th>Region</th>
                  <th>Budget hint</th>
                  <th>Verified</th>
                  <th>Verifier</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr
                    key={v.id}
                    style={{ background: selected.has(v.id) ? 'var(--ivory-2)' : 'transparent' }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(v.id)}
                        onChange={() => toggle(v.id)}
                      />
                    </td>
                    <td>
                      <div
                        style={{ fontWeight: 500, cursor: 'pointer' }}
                        onClick={() => navigate(`/lead-manager/leads/${v.id}`)}
                      >
                        {leadName(v)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        <span className="mono">{v.id.slice(-6)}</span>
                        {(v.notes || v.note) && <> · {v.notes || v.note}</>}
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {leadPhone(v)}
                    </td>
                    <td>{leadProduct(v)}</td>
                    <td>{leadRegion(v)}</td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {v.budget ?? '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {v.verifiedAt ? `${relativeTime(v.verifiedAt)} ago` : '—'}
                    </td>
                    <td>
                      <span className="chip">{v.verifierName ?? 'Me'}</span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setSinglePickLeadId(v.id);
                          setPickerOpen(true);
                        }}
                      >
                        Assign →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {pickerOpen && (
        <TeamPickerModal
          leadIds={targetLeadIds}
          teams={teams}
          onClose={() => {
            setPickerOpen(false);
            setSinglePickLeadId(null);
          }}
          onAssigned={handleAssigned}
        />
      )}
    </PageShell>
  );
}

export default VerifiedPoolPage;
